import { createTheme, alpha } from '@mui/material/styles';

const defaultTheme = createTheme();

export const FONT_FAMILY = '"Archivo Variable", system-ui, -apple-system, "Segoe UI", sans-serif';

const customShadows = [...defaultTheme.shadows];

// Verdi Ecotrade, dal logo di Minerva: 400 = verde Ecotrade (#365942), 300 = oliva
// (#809D55), 500 = muschio scurito per i bottoni pieni. Stessa logica della scala
// precedente (400 principale, 500 pieno, 600 hover, 700-900 scuri), tonalità più calda.
export const brand = {
  50: '#F3F6EF',
  100: '#E4EBDA',
  200: '#CAD8B6',
  300: '#809D55',
  400: '#365942',
  500: '#2F4E3A',
  600: '#4C6E55',
  700: '#2A4534',
  800: '#1E3326',
  900: '#13221A',
};

// Colori del logo, per gli accenti (mai per lo stato dei dati).
export const ecotrade = { verde: '#365942', oliva: '#809D55', muschio: '#627C44' };

// Neutri freddi con una punta di verde, per non stonare accanto al brand.
export const gray = {
  50: '#F7F9F8',
  100: '#EEF1EF',
  200: '#DDE3DF',
  300: '#C9D1CC',
  400: '#98A39C',
  500: '#5B665F',
  600: '#47514B',
  700: '#2F3833',
  800: '#16201B',
  900: '#0C120F',
};

export const green = {
  50: 'hsl(120, 80%, 98%)',
  100: 'hsl(120, 75%, 94%)',
  200: 'hsl(120, 75%, 87%)',
  300: 'hsl(120, 61%, 77%)',
  400: 'hsl(120, 44%, 53%)',
  500: 'hsl(120, 59%, 30%)',
  600: 'hsl(120, 70%, 25%)',
  700: 'hsl(120, 75%, 16%)',
  800: 'hsl(120, 84%, 10%)',
  900: 'hsl(120, 87%, 6%)',
};

export const orange = {
  50: 'hsl(45, 100%, 97%)',
  100: 'hsl(45, 92%, 90%)',
  200: 'hsl(45, 94%, 80%)',
  300: 'hsl(45, 90%, 65%)',
  400: 'hsl(45, 90%, 40%)',
  500: 'hsl(45, 90%, 35%)',
  600: 'hsl(45, 91%, 25%)',
  700: 'hsl(45, 94%, 20%)',
  800: 'hsl(45, 95%, 16%)',
  900: 'hsl(45, 93%, 12%)',
};

export const red = {
  50: 'hsl(0, 100%, 97%)',
  100: 'hsl(0, 92%, 90%)',
  200: 'hsl(0, 94%, 80%)',
  300: 'hsl(0, 90%, 65%)',
  400: 'hsl(0, 90%, 40%)',
  500: 'hsl(0, 90%, 30%)',
  600: 'hsl(0, 91%, 25%)',
  700: 'hsl(0, 94%, 18%)',
  800: 'hsl(0, 95%, 12%)',
  900: 'hsl(0, 93%, 6%)',
};

export const getDesignTokens = (mode) => {
  customShadows[1] =
    mode === 'dark'
      ? 'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px'
      : 'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px';

  return {
    palette: {
      mode,
      primary: {
        light: brand[200],
        main: brand[400],
        dark: brand[700],
        contrastText: brand[50],
        ...(mode === 'dark' && {
          contrastText: brand[50],
          light: brand[300],
          main: brand[400],
          dark: brand[700],
        }),
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
        ...(mode === 'dark' && {
          contrastText: brand[300],
          light: brand[500],
          main: brand[700],
          dark: brand[900],
        }),
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
        ...(mode === 'dark' && {
          light: orange[400],
          main: orange[500],
          dark: orange[700],
        }),
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
        ...(mode === 'dark' && {
          light: red[400],
          main: red[500],
          dark: red[700],
        }),
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
        ...(mode === 'dark' && {
          light: green[400],
          main: green[500],
          dark: green[700],
        }),
      },
      grey: {
        ...gray,
      },
      divider: mode === 'dark' ? alpha(gray[700], 0.6) : alpha(gray[300], 0.4),
      background: {
        default: 'hsl(0, 0%, 99%)',
        paper: 'hsl(220, 35%, 97%)',
        ...(mode === 'dark' && { default: gray[900], paper: 'hsl(220, 30%, 7%)' }),
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        warning: orange[400],
        ...(mode === 'dark' && {
          primary: 'hsl(0, 0%, 100%)',
          secondary: gray[400],
        }),
      },
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
        ...(mode === 'dark' && {
          hover: alpha(gray[600], 0.2),
          selected: alpha(gray[600], 0.3),
        }),
      },
    },
    typography: {
      fontFamily: FONT_FAMILY,
      h1: {
        fontSize: defaultTheme.typography.pxToRem(48),
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: -0.5,
      },
      h2: {
        fontSize: defaultTheme.typography.pxToRem(36),
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h3: {
        fontSize: defaultTheme.typography.pxToRem(30),
        lineHeight: 1.2,
      },
      h4: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h5: {
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: 600,
      },
      h6: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 600,
      },
      subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18),
      },
      subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 500,
      },
      body1: {
        fontSize: defaultTheme.typography.pxToRem(14),
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400,
      },
      caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 400,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: customShadows,
  };
};

// Ruoli colore di Minerva. Ogni colore ha un solo compito:
// Minerva/Bosco = struttura e azioni, rosso = errori e cali, Gas/Power = tag commodity.
export const minerva = {
  bosco: brand[800],
  minerva: brand[400],
  salvia: brand[200],
  oliva: ecotrade.oliva,
  muschio: ecotrade.muschio,
  nebbia: gray[100],
  inchiostro: gray[800],
  ribasso: '#B8432F',
  rialzo: '#1E8A5A',
  gas: '#2F6690',
  power: '#D08A1E',
};

export const colorSchemes = {
  light: {
    palette: {
      primary: { light: brand[300], main: brand[400], dark: brand[800], contrastText: '#FFFFFF' },
      secondary: { light: gray[300], main: gray[600], dark: gray[800], contrastText: '#FFFFFF' },
      info: { light: '#DDE8F1', main: minerva.gas, dark: '#1F4A6B', contrastText: '#FFFFFF' },
      warning: { light: '#F6E7CF', main: '#B7791F', dark: '#7A4F12', contrastText: '#FFFFFF' },
      error: { light: '#F4D9D3', main: minerva.ribasso, dark: '#7E2B1D', contrastText: '#FFFFFF' },
      success: { light: '#D5EEE1', main: minerva.rialzo, dark: '#145E3D', contrastText: '#FFFFFF' },
      gas: { light: '#DDE8F1', main: minerva.gas, dark: '#1F4A6B', contrastText: '#FFFFFF' },
      power: { light: '#F6E7CF', main: minerva.power, dark: '#8A5A12', contrastText: '#FFFFFF' },
      grey: { ...gray },
      divider: gray[200],
      background: { default: gray[100], paper: '#FFFFFF' },
      text: { primary: gray[800], secondary: gray[500], warning: '#B7791F' },
      action: { hover: alpha(gray[300], 0.35), selected: alpha(brand[200], 0.5) },
      baseShadow: '0 1px 2px rgba(22, 32, 27, 0.06), 0 4px 12px -4px rgba(22, 32, 27, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: { light: '#CAD8B6', main: '#A7BF84', dark: '#809D55', contrastText: gray[900] },
      secondary: { light: gray[300], main: gray[400], dark: gray[600], contrastText: gray[900] },
      info: { light: '#2A4A63', main: '#7FA9CC', dark: '#BBD3E6', contrastText: gray[900] },
      warning: { light: '#5A4116', main: '#E2A94B', dark: '#F2D29C', contrastText: gray[900] },
      error: { light: '#5C2A20', main: '#E07A63', dark: '#F2B6A8', contrastText: gray[900] },
      success: { light: '#1D4A35', main: '#5CC08E', dark: '#A8E2C4', contrastText: gray[900] },
      gas: { light: '#2A4A63', main: '#7FA9CC', dark: '#BBD3E6', contrastText: gray[900] },
      power: { light: '#5A4116', main: '#E2A94B', dark: '#F2D29C', contrastText: gray[900] },
      grey: { ...gray },
      divider: alpha(gray[600], 0.6),
      background: { default: '#0E1512', paper: '#151E19' },
      text: { primary: '#E8EEEA', secondary: '#9AA8A0' },
      action: { hover: alpha(gray[600], 0.25), selected: alpha(brand[400], 0.25) },
      baseShadow: '0 1px 2px rgba(0, 0, 0, 0.5), 0 4px 12px -4px rgba(0, 0, 0, 0.6)',
    },
  },
};

const display = { fontVariationSettings: '"wdth" 110', letterSpacing: '-0.01em' };

export const typography = {
  fontFamily: FONT_FAMILY,
  h1: {
    ...display,
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  h2: {
    ...display,
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h3: {
    ...display,
    fontSize: defaultTheme.typography.pxToRem(30),
    lineHeight: 1.2,
  },
  h4: {
    ...display,
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(18),
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 8,
};

const defaultShadows = [
  'none',
  'var(--template-palette-baseShadow)',
  ...defaultTheme.shadows.slice(2),
];

export const shadows = defaultShadows;
